import React, { useRef } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useSpring,
  useInView,
} from 'framer-motion';
 
/* ============================================================
   Reusable primitives
   ============================================================ */

const SplitTitle = ({ text, className = '', delay = 0, italic = false }) => {
  const words = text.split(' ');
  return (
    <motion.span
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.09, delayChildren: delay } },
      }}
      className={`inline-block ${italic ? 'italic' : ''} ${className}`}
    >
      {words.map((w, i) => (
        <span key={i} className="inline-block overflow-hidden align-bottom mr-[0.22em]">
          <motion.span
            className="inline-block"
            variants={{
              hidden: { y: '110%' },
              visible: {
                y: 0,
                transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] },
              },
            }}
          >
            {w}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
};

const Reveal = ({ children, delay = 0, y = 24, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const InkRule = ({ className = '', delay = 0 }) => (
  <motion.div
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={{ once: true, amount: 0.6 }}
    transition={{ duration: 1.4, delay, ease: [0.65, 0, 0.35, 1] }}
    className={`origin-left h-px bg-[#3a2a1c]/40 ${className}`}
  />
);

const Plate = ({ src, alt, figure, caption, className = '', height = 'h-[420px] md:h-[640px]' }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-7%', '7%']);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.05, 1, 1.05]);

  return (
    <figure ref={ref} className={`relative ${className}`}>
      <div className="relative p-[6px] bg-[#1a1410]/8 border border-[#3a2a1c]/25">
        <div className={`relative overflow-hidden ${height} bg-[#1a1410]`}>
          <motion.img
            src={src}
            alt={alt}
            style={{ y, scale }}
            className="absolute inset-0 h-[115%] w-full object-cover"
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              boxShadow: 'inset 0 0 120px rgba(20,12,6,0.55)',
              background:
                'radial-gradient(ellipse at center, transparent 50%, rgba(20,12,6,0.25) 100%)',
            }}
          />
        </div>
      </div>
      {(figure || caption) && (
        <Reveal delay={0.2}>
          <figcaption className="mt-5 flex items-baseline gap-4 text-[#3a2a1c]/75">
            {figure && (
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] shrink-0">
                {figure}
              </span>
            )}
            {figure && <span className="h-px w-8 bg-[#3a2a1c]/30 shrink-0 translate-y-[-3px]" />}
            {caption && <span className="font-serif italic text-sm md:text-base">{caption}</span>}
          </figcaption>
        </Reveal>
      )}
    </figure>
  );
};

const DropCap = ({ children }) => (
  <motion.span
    initial={{ scale: 0, rotate: -8, opacity: 0 }}
    whileInView={{ scale: 1, rotate: 0, opacity: 1 }}
    viewport={{ once: true, amount: 0.6 }}
    transition={{ duration: 0.9, ease: [0.34, 1.56, 0.64, 1], delay: 0.2 }}
    className="float-left font-serif text-[5.5rem] md:text-[7rem] leading-[0.85] mr-3 mt-1 text-[#6B2C2C]"
    style={{ fontFeatureSettings: '"liga","dlig"' }}
  >
    {children}
  </motion.span>
);

const Fleuron = () => (
  <Reveal>
    <div className="flex items-center justify-center gap-6 my-16 md:my-24">
      <span className="h-px w-16 md:w-24 bg-[#3a2a1c]/35" />
      <svg width="22" height="22" viewBox="0 0 24 24" className="text-[#6B2C2C]">
        <path
          d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"
          fill="currentColor"
          opacity="0.85"
        />
      </svg>
      <span className="h-px w-16 md:w-24 bg-[#3a2a1c]/35" />
    </div>
  </Reveal>
);

const PullQuote = ({ children, attribution }) => (
  <Reveal delay={0.1} y={36}>
    <blockquote className="relative py-10 md:py-16 max-w-3xl mx-auto text-center">
      <span
        aria-hidden
        className="absolute -top-4 left-1/2 -translate-x-1/2 font-serif text-7xl md:text-8xl text-[#A05A2C]/60 leading-none select-none"
      >
        "
      </span>
      <p className="font-serif italic text-2xl md:text-4xl leading-[1.3] text-[#1a1410] tracking-tight">
        {children}
      </p>
      {attribution && (
        <footer className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-[#3a2a1c]/60">
          {attribution}
        </footer>
      )}
    </blockquote>
  </Reveal>
);

const ChapterHeading = ({ numeral, label, title }) => (
  <header className="mb-14 md:mb-20">
    <Reveal>
      <div className="flex items-baseline gap-6 mb-6">
        <span className="font-serif italic text-2xl md:text-3xl text-[#A05A2C]">
          {numeral}.
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#3a2a1c]/65">
          {label}
        </span>
      </div>
    </Reveal>
    <InkRule delay={0.15} className="mb-8 max-w-[180px]" />
    <Reveal delay={0.2}>
      <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight text-[#1a1410] max-w-4xl">
        {title}
      </h2>
    </Reveal>
  </header>
);

const PaperLayers = () => (
  <>
    <div
      className="pointer-events-none fixed inset-0 z-0 opacity-[0.18] mix-blend-multiply"
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 320 320' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='2' stitchTiles='stitch'/%3E%3CfeColorMatrix values='0 0 0 0 0.18 0 0 0 0 0.12 0 0 0 0 0.06 0 0 0 0.55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
      }}
    />
    <div
      className="pointer-events-none fixed inset-0 z-0"
      style={{
        background:
          'radial-gradient(ellipse 80% 60% at 50% 40%, transparent 30%, rgba(58,38,18,0.18) 100%)',
      }}
    />
  </>
);

const SectionBackdrop = ({ tone = 'parchment' }) => {
  const tones = {
    parchment: {
      base: '#ECE3D0',
      mid: '#E2D5B8',
      stain: 'rgba(120,70,30,0.07)',
      blot: 'rgba(60,30,10,0.08)',
    },
    sepia: {
      base: '#D8C9A8',
      mid: '#C8B68F',
      stain: 'rgba(120,70,30,0.10)',
      blot: 'rgba(60,30,10,0.10)',
    },
    foxed: {
      base: '#E5D6B8',
      mid: '#D4C094',
      stain: 'rgba(140,70,20,0.12)',
      blot: 'rgba(80,40,10,0.09)',
    },
  };
  const t = tones[tone];
  return (
    <div
      className="absolute inset-0 z-0"
      style={{
        backgroundColor: t.base,
        backgroundImage: `
          radial-gradient(ellipse at 12% 18%, ${t.stain} 0%, transparent 35%),
          radial-gradient(ellipse at 82% 70%, ${t.blot} 0%, transparent 40%),
          radial-gradient(circle at 65% 22%, ${t.stain} 0%, transparent 18%),
          radial-gradient(circle at 25% 85%, ${t.blot} 0%, transparent 25%),
          radial-gradient(ellipse at center, ${t.base} 0%, ${t.mid} 100%)
        `,
      }}
    />
  );
};

/* ============================================================
   The page
   ============================================================ */
const Landing = () => {
  const { scrollYProgress } = useScroll();
  const progressX = useSpring(scrollYProgress, { stiffness: 90, damping: 28 });

  const fragmentRef = useRef(null);
  const fragmentInView = useInView(fragmentRef, { once: true, amount: 0.4 });

  return (
    <main className="relative min-h-screen text-[#1a1410] overflow-hidden bg-[#ECE3D0]">
      <PaperLayers />

      <motion.div
        style={{ scaleX: progressX }}
        className="fixed top-0 left-0 right-0 h-[2px] bg-[#A05A2C] origin-left z-50"
      />

      {/* ═══════════════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════════════ */}
      <section className="relative">
        <SectionBackdrop tone="parchment" />
        <div className="relative z-10 px-6 md:px-10 pt-24 pb-32 md:pt-40 md:pb-48">
          <div className="mx-auto max-w-5xl text-center">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.3 }}
              className="font-mono text-[10px] md:text-xs uppercase tracking-[0.5em] text-[#3a2a1c]/70 mb-12"
            >
              On the recovery of Andean memory
            </motion.div>

            <h1 className="font-serif leading-[0.95] tracking-[-0.02em]">
              <span className="block text-6xl md:text-8xl lg:text-[9.5rem] text-[#1a1410]">
                <SplitTitle text="The Quipu" />
              </span>
              <span className="block text-6xl md:text-8xl lg:text-[9.5rem] text-[#1a1410] mt-1">
                <SplitTitle text="Project" italic delay={0.5} />
                <span className="not-italic">.</span>
              </span>
            </h1>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.6, delay: 1.4, ease: [0.65, 0, 0.35, 1] }}
              className="origin-center h-px bg-[#3a2a1c]/45 max-w-md mx-auto mt-14"
            />

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 1.7 }}
              className="font-serif italic text-lg md:text-2xl text-[#3a2a1c]/85 mt-10 max-w-2xl mx-auto leading-relaxed"
            >
              Digitizing Peru's colonial archives to find the translation key
              for the Andean quipus, the knotted cords of the Inca Empire.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 2, delay: 2 }}
              className="mt-20 flex justify-center"
            >
              <svg width="180" height="80" viewBox="0 0 180 80" className="text-[#6B2C2C]">
                <motion.line
                  x1="10" y1="20" x2="170" y2="20"
                  stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.4, delay: 2.1 }}
                />
                {[
                  { x: 30, len: 50, knots: [25, 38] },
                  { x: 55, len: 60, knots: [30, 45, 55] },
                  { x: 80, len: 45, knots: [35] },
                  { x: 105, len: 65, knots: [28, 42, 58] },
                  { x: 130, len: 40, knots: [32] },
                  { x: 155, len: 55, knots: [30, 48] },
                ].map((c, i) => (
                  <g key={i}>
                    <motion.line
                      x1={c.x} y1="20" x2={c.x} y2={20 + c.len}
                      stroke="currentColor" strokeWidth="0.9" strokeLinecap="round"
                      opacity="0.85"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ duration: 0.8, delay: 2.4 + i * 0.08 }}
                    />
                    {c.knots.map((y, j) => (
                      <motion.circle
                        key={j} cx={c.x} cy={y} r="2.2"
                        fill="currentColor"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.4, delay: 2.9 + i * 0.05 + j * 0.05 }}
                      />
                    ))}
                  </g>
                ))}
              </svg>
            </motion.div>

            <motion.a
              href="#opening"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, delay: 3 }}
              className="group inline-block mt-16 font-mono text-[10px] uppercase tracking-[0.4em] text-[#3a2a1c]/70 hover:text-[#6B2C2C] transition-colors"
            >
              ↓&nbsp;&nbsp;Begin reading
            </motion.a>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          i. OPENING
          ═══════════════════════════════════════════════════════ */}
      <section id="opening" className="relative">
        <SectionBackdrop tone="parchment" />
        <div className="relative z-10 px-6 md:px-10 py-24 md:py-40">
          <div className="mx-auto max-w-5xl">
            <ChapterHeading
              numeral="i"
              label="Preface"
              title={
                <>
                  A civilization whose writing system
                  <span className="italic"> no one alive can read</span>.
                </>
              }
            />

            <Reveal delay={0.2}>
              <div className="font-serif text-lg md:text-xl leading-[1.75] text-[#1a1410]/95 max-w-3xl">
                <p className="mb-6">
                  <DropCap>T</DropCap>
                  he Inca Empire left no stone inscriptions, no scrolls, no
                  clay tablets. It kept its records in quipus: knotted cords
                  that encoded numerical data with remarkable precision.
                  Scholars broadly agree the quipus also carried narrative
                  information, including genealogies and histories. That
                  meaning remains locked.
                </p>
                <p>
                  The key may not require a new theory. It may simply require
                  finding the right document, a colonial record in which a
                  Spanish scribe or a literate Andean hand set down, side by side,
                  the knots and their words. Our project is the systematic search
                  for that record, at a scale manual methods could never reach.
                </p>
              </div>
            </Reveal>

            <div className="mt-20 md:mt-28">
              <Plate
                src="https://miviaje.com/wp-content/uploads/2019/07/muralla-kuelap.jpg"
                alt="The fortified walls of Kuélap"
                figure="Plate I"
                caption="Kuélap, Amazonas. The Chachapoya fortress."
                height="h-[380px] md:h-[620px]"
              />
            </div>
          </div>
        </div>
      </section>

      <Fleuron />

      {/* ═══════════════════════════════════════════════════════
          ii. THE ARCHIVES
          ═══════════════════════════════════════════════════════ */}
      <section className="relative">
        <SectionBackdrop tone="sepia" />
        <div className="relative z-10 px-6 md:px-10 py-24 md:py-40">
          <div className="mx-auto max-w-7xl">
            <ChapterHeading
              numeral="ii"
              label="The Field"
              title={
                <>
                  The evidence already exists.
                  <span className="italic"> It has not yet been read.</span>
                </>
              }
            />

            <div className="grid md:grid-cols-12 gap-10 lg:gap-16">
              <aside className="md:col-span-2 hidden md:block">
                <Reveal delay={0.3}>
                  <div className="sticky top-24 space-y-6 pt-2">
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#6B2C2C]">
                      Note A
                    </p>
                    <p className="font-serif italic text-sm leading-relaxed text-[#3a2a1c]/80">
                      Late 20th century legislation mandated every district to
                      transfer its oldest documents to its regional capital.
                    </p>
                    <span className="block h-px w-12 bg-[#3a2a1c]/30" />
                    <p className="font-serif italic text-sm leading-relaxed text-[#3a2a1c]/80">
                      The target records are the least explored stratum of Peru's
                      colonial archive.
                    </p>
                  </div>
                </Reveal>
              </aside>

              <div className="md:col-span-6">
                <Reveal>
                  <div className="font-serif text-base md:text-lg leading-[1.85] text-[#1a1410]/95 space-y-5">
                    <p>
                      Peru's 25 regional archives hold centuries of colonial
                      documentation: notarial registers, ecclesiastical records,
                      corregimiento ledgers, missionary reports. By law, these
                      archives are the consolidated repositories for the documentary
                      output of their entire regions, making them the densest
                      targets for systematic search.
                    </p>
                    <p>
                      The obstacle has never been access. It has been volume. The
                      handwriting is difficult, the paper is fragile, and the sheer
                      number of pages has defeated every manual approach attempted
                      over the past two centuries. A trained researcher can
                      transcribe only a handful of pages each day. The archives
                      contain millions.
                    </p>
                    <p>
                      We are working through them at a different order of magnitude.
                    </p>
                  </div>
                </Reveal>
              </div>

              <div className="md:col-span-4">
                <Plate
                  src="https://image-tc.galaxy.tf/wijpeg-2ic4nlo4utihudzws7l3pjswv/historia-de-machu-picchu-peru_wide.jpg?crop=0%2C56%2C1800%2C1013"
                  alt="Andean cordillera"
                  figure="Plate II"
                  caption="The cordillera, in whose folds the parish archives endure."
                  height="h-[420px] md:h-[560px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Fleuron />

      {/* ═══════════════════════════════════════════════════════
          iii. THE ROSETTA STONE
          ═══════════════════════════════════════════════════════ */}
      <section className="relative">
        <SectionBackdrop tone="foxed" />
        <div className="relative z-10 px-6 md:px-10 py-24 md:py-40">
          <div className="mx-auto max-w-5xl">
            <ChapterHeading
              numeral="iii"
              label="The Object"
              title={
                <>
                  Somewhere in these pages lies the
                  <span className="italic"> key to the knots</span>.
                </>
              }
            />

            <Reveal>
              <p className="font-serif text-lg md:text-xl leading-[1.75] text-[#1a1410]/95 max-w-3xl mb-16">
                Thousands of quipus survive in museum collections, and many more
                remain in private hands and rural communities. Their numerical
                logic is well understood. What remains opaque is the narrative
                layer: the genealogies, historical accounts, and social records
                that colonial witnesses described but never translated. In 2011,
                a single census from 1670 allowed researchers to conclude that
                quipus encoded social class and lineage. Earlier records, written
                while the original quipucamayocs were still alive, stand a far
                greater chance of containing a direct correspondence.
              </p>
            </Reveal>

            <PullQuote>
              We are searching for the document in which a scribe set down
              the knots beside the words.
            </PullQuote>

            <motion.div
              ref={fragmentRef}
              initial={{ opacity: 0, rotate: -3, y: 30 }}
              animate={fragmentInView ? { opacity: 1, rotate: -1.4, y: 0 } : {}}
              transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              className="mt-16 max-w-3xl mx-auto relative"
              style={{ transformOrigin: 'center' }}
            >
              <div
                className="relative p-10 md:p-14 shadow-2xl"
                style={{
                  background: `
                    radial-gradient(ellipse at 30% 20%, rgba(160,90,40,0.10) 0%, transparent 40%),
                    radial-gradient(ellipse at 80% 75%, rgba(80,40,10,0.13) 0%, transparent 35%),
                    linear-gradient(180deg, #E8D9B5 0%, #DDC89C 100%)
                  `,
                  boxShadow:
                    '0 30px 60px -20px rgba(60,30,10,0.45), 0 0 0 1px rgba(60,30,10,0.08), inset 0 0 80px rgba(120,70,30,0.10)',
                  clipPath:
                    'polygon(0% 2%, 3% 0%, 97% 1%, 100% 4%, 99% 96%, 96% 100%, 4% 99%, 1% 95%)',
                }}
              >
                <div className="flex items-center justify-between mb-8 font-mono text-[10px] uppercase tracking-[0.25em] text-[#3a2a1c]/65">
                  <span>AGI · Lima · Leg. 471, fol. 23v</span>
                  <span className="italic">[transcribed fragment]</span>
                </div>

                <div className="font-serif text-sm md:text-base leading-[1.9] text-[#1a1410]/90 italic">
                  <p className="mb-4">
                    "<span className="not-italic">Y</span> el dicho indio quipocamayoc
                    declaró por sus ñudos que en el ayllu de los Chillaos habían{' '}
                    <span className="text-[#6B2C2C] not-italic underline decoration-[#6B2C2C]/40 decoration-wavy underline-offset-4">
                      trescientos y veinte y dos
                    </span>{' '}
                    indios tributarios, y entre ellos cuarenta viudas, las quales
                    tenían en sus chacras maíz, papas, y ocho llamas..."
                  </p>
                </div>

                <div className="mt-8 pl-6 border-l-2 border-[#6B2C2C]/50 font-serif text-xs md:text-sm text-[#6B2C2C]/85">
                  <span className="font-mono text-[9px] uppercase tracking-[0.3em] mr-3">
                    margin
                  </span>
                  <span className="italic">
                    quipu, primary cord rust-brown; 6 pendant cords; long knots
                    matching the recorded count.
                  </span>
                </div>

                <div
                  aria-hidden
                  className="absolute top-4 right-12 w-20 h-16 rounded-full opacity-20 pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(ellipse, rgba(140,70,20,0.7) 0%, transparent 70%)',
                  }}
                />
                <div
                  aria-hidden
                  className="absolute bottom-8 left-16 w-24 h-12 rounded-full opacity-15 pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(ellipse, rgba(80,40,10,0.7) 0%, transparent 70%)',
                  }}
                />
              </div>

              <Reveal delay={0.3}>
                <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#3a2a1c]/60">
                  representation of the document we are searching for
                </p>
              </Reveal>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          iv. METHOD — DARK PLATE
          ═══════════════════════════════════════════════════════ */}
      <section className="relative bg-[#1A1410]">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 320 320' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 60% 40% at 50% 30%, rgba(160,90,44,0.10) 0%, transparent 70%)',
          }}
        />

        <div className="relative z-10 px-6 md:px-10 py-24 md:py-40 text-[#ECE3D0]">
          <div className="mx-auto max-w-7xl">
            <header className="mb-14 md:mb-20 max-w-4xl">
              <Reveal>
                <div className="flex items-baseline gap-6 mb-6">
                  <span className="font-serif italic text-2xl md:text-3xl text-[#A05A2C]">
                    iv.
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#ECE3D0]/65">
                    The Method
                  </span>
                </div>
              </Reveal>
              <InkRule delay={0.15} className="mb-8 max-w-[180px] !bg-[#ECE3D0]/40" />
              <Reveal delay={0.2}>
                <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
                  Three steps: photograph,{' '}
                  <span className="italic">transcribe, search</span>.
                </h2>
              </Reveal>
            </header>
 
            <div className="grid md:grid-cols-12 gap-10 lg:gap-16 items-start">
              <div className="md:col-span-7">
                <Plate 
                  src="https://maresone.s3.us-east-1.amazonaws.com/graphsone.jpeg"
                  alt="Library archives"
                  figure="Plate III"
                  caption="A reading room. Volume, here, is the first adversary."
                  height="h-[420px] md:h-[640px]"
                />
              </div>
              <div className="md:col-span-5 md:pl-2">
                <Reveal>
                  <div className="font-serif text-base md:text-lg leading-[1.85] text-[#ECE3D0]/85 space-y-5">
                    <p>
                      The first phase is physical: photographers placed in each of
                      Peru's regional archives, working methodically through the
                      colonial holdings no prior digitization effort has reached,
                      including notarial protocols, corregimiento records, visitas,
                      and missionary documentation.
                    </p>
                    <p>
                      Once photographed, the documents are processed by a multimodal
                      AI system trained to read colonial Spanish handwriting with
                      accuracy rivaling expert annotators. The transcriptions are
                      then structured and indexed, making it possible to search
                      across millions of pages for the specific patterns, such as
                      references to quipucamayocs and knot based accounts, that
                      would mark a translation key document.
                    </p>
                    <p>
                      The complete dataset of images, transcriptions, and metadata
                      will be made freely available to researchers worldwide.
                    </p>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          v. CLOSING
          ═══════════════════════════════════════════════════════ */}
      <section className="relative">
        <SectionBackdrop tone="parchment" />
        <div className="relative z-10 px-6 md:px-10 py-24 md:py-40">
          <div className="mx-auto max-w-5xl">
            <ChapterHeading
              numeral="v"
              label="The Wider Effort"
              title={
                <>
                  Whatever we find, the archive
                  <span className="italic"> will not be lost again</span>.
                </>
              }
            />

            <Reveal>
              <p className="font-serif text-lg md:text-xl leading-[1.75] text-[#1a1410]/95 max-w-3xl mb-12">
                The primary goal is the quipu translation key. Even setting that
                aside, the project will produce the largest searchable database
                of Peru's colonial documentation ever assembled, a resource for
                historians, linguists, legal researchers, and the communities
                whose ancestors appear in these pages. The work begins in the
                archives most likely to hold 16th century records written while
                the original Inca record keepers were still alive, and expands
                outward from there.
              </p>
            </Reveal>

            <PullQuote>
              The past does not need to be invented. It needs only to be returned
              to those who can recognize it.
            </PullQuote>

            <div className="mt-16">
              <Plate
                src="https://maresone.s3.us-east-1.amazonaws.com/zerouno.jpeg"
                alt="An aged manuscript"
                figure="Folio"
                caption="Sample handwritten document from the 18th century"
                height="h-[360px] md:h-[560px]"
              />
            </div> 
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          COLOPHON
          ═══════════════════════════════════════════════════════ */}
      <section className="relative">
        <SectionBackdrop tone="sepia" />
        <div className="relative z-10 px-6 md:px-10 py-24 md:py-32 border-t border-[#3a2a1c]/25">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#3a2a1c]/65 mb-10">
                Colophon
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="font-serif italic text-xl md:text-2xl leading-[1.7] text-[#1a1410]">
                By digitizing and analyzing Peru's colonial archives at scale,
                we aim to return the written memory of the Inca world to the
                people whose history it holds.
              </p>
            </Reveal>

            <InkRule delay={0.3} className="mx-auto mt-12 max-w-[140px] origin-center" />

            <Reveal delay={0.4}>
              <p className="mt-10 font-serif text-base md:text-lg text-[#1a1410]/85">
                Correspondence is welcome at{' '}
                <a
                  href="mailto:hector.diaz@routedlabs.com"
                  className="italic text-[#6B2C2C] underline decoration-[#6B2C2C]/40 underline-offset-4 hover:decoration-[#6B2C2C] transition-all"
                >
                  hector.diaz@routedlabs.com
                </a>
              </p>
            </Reveal>

            <Reveal delay={0.5}>
              <p className="mt-12 font-mono text-[10px] uppercase tracking-[0.35em] text-[#3a2a1c]/55">
                Que Dios bendiga al Perú
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500;1,600&family=Crimson+Pro:ital,wght@0,400;0,500;1,400;1,500&family=JetBrains+Mono:wght@400;500&display=swap');
        main { font-family: 'Crimson Pro', 'EB Garamond', Georgia, serif; }
        .font-serif { font-family: 'Cormorant Garamond', 'EB Garamond', Georgia, serif; }
        .font-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
        html { scroll-behavior: smooth; }
        ::selection { background: #6B2C2C; color: #ECE3D0; }
      `}</style>
    </main>
  );
};

export default Landing;